import { decodeFunctionData, encodeFunctionData } from 'viem'
import type { Address, Hex } from 'viem'
import { bakeryRouterAbi, pcsV3RouterAbi, swapRouter02Abi, v2RouterAbi } from './abi'
import { PoolDex, WBNB, lc } from './dexes'

/**
 * Single-pool trades. Everything here is pure (no network): it builds the transaction for ONE pool, decodes the bytes that are about to be signed,
 * and refuses anything that is not exactly one hop through that pool to the connected wallet.
 *
 * Why decode: the quote and the builder are code we wrote, and a bug or a swapped argument there would send a trade through another pool. The guard
 * therefore does not trust the builder. It reads the final calldata, finds the hops the router will actually take, asks the exchange's factory which
 * pool those hops resolve to, and compares it with the pool the page promised. It is the last thing that runs before the wallet opens.
 */

/** SwapRouter02 family: "keep the output in the router" (then unwrap it to the user). */
export const ADDRESS_THIS: Address = '0x0000000000000000000000000000000000000002'
const ZERO: Address = '0x0000000000000000000000000000000000000000'

export interface PoolFacts {
  pool: Address
  factory: Address
  token0: Address
  token1: Address
  /** V3 fee tier in hundredths of a bip (2500 = 0.25 %); null for V2. */
  fee: number | null
}

export interface TradeParams {
  dex: PoolDex
  facts: PoolFacts
  /** The ERC-20 legs. A native BNB leg is WBNB here plus nativeIn / nativeOut. */
  tokenIn: Address
  tokenOut: Address
  amountIn: bigint
  minOut: bigint
  /** The connected wallet: where the output goes. */
  recipient: Address
  deadline: bigint
  nativeIn: boolean
  nativeOut: boolean
}

export interface BuiltTrade {
  to: Address
  data: Hex
  value: bigint
  /** Contract that needs an ERC-20 allowance for amountIn; null when BNB is sent as value. */
  spender: Address | null
}

export type RefusalCode =
  | 'unsupported_dex'
  | 'unsupported_call'
  | 'multi_hop'
  | 'wrong_router'
  | 'wrong_factory'
  | 'wrong_tokens'
  | 'wrong_fee'
  | 'wrong_payout'
  | 'wrong_value'
  | 'pool_mismatch'
  | 'pool_not_found'
  | 'native_unsupported'

export class RouteRefused extends Error {
  readonly code: RefusalCode

  constructor(code: RefusalCode, message: string) {
    super(message)
    this.name = 'RouteRefused'
    this.code = code
  }
}

/** Builds the transaction for one pool. Throws RouteRefused when this exchange's router cannot do the requested leg. */
export function buildTrade(p: TradeParams): BuiltTrade {
  const { dex, facts, tokenIn, tokenOut, amountIn, minOut, recipient, deadline, nativeIn, nativeOut } = p
  if (nativeIn && nativeOut) throw new RouteRefused('native_unsupported', 'BNB cannot be both sides of a trade')
  if (nativeIn && lc(tokenIn) !== lc(WBNB)) throw new RouteRefused('native_unsupported', 'BNB can only be used where the pool token is WBNB')
  if (nativeOut && lc(tokenOut) !== lc(WBNB)) throw new RouteRefused('native_unsupported', 'BNB can only be used where the pool token is WBNB')
  const { router } = dex
  const value = nativeIn ? amountIn : 0n
  const spender = nativeIn ? null : router

  if (dex.style === 'v2' || dex.style === 'v2-bakery') {
    const path: Address[] = [tokenIn, tokenOut]
    if (dex.style === 'v2') {
      const data = nativeIn
        ? encodeFunctionData({ abi: v2RouterAbi, functionName: 'swapExactETHForTokens', args: [minOut, path, recipient, deadline] })
        : nativeOut
        ? encodeFunctionData({ abi: v2RouterAbi, functionName: 'swapExactTokensForETH', args: [amountIn, minOut, path, recipient, deadline] })
        : encodeFunctionData({ abi: v2RouterAbi, functionName: 'swapExactTokensForTokens', args: [amountIn, minOut, path, recipient, deadline] })
      return { to: router, data, value, spender }
    }
    const data = nativeIn
      ? encodeFunctionData({ abi: bakeryRouterAbi, functionName: 'swapExactBNBForTokens', args: [minOut, path, recipient, deadline] })
      : nativeOut
      ? encodeFunctionData({ abi: bakeryRouterAbi, functionName: 'swapExactTokensForBNB', args: [amountIn, minOut, path, recipient, deadline] })
      : encodeFunctionData({ abi: bakeryRouterAbi, functionName: 'swapExactTokensForTokens', args: [amountIn, minOut, path, recipient, deadline] })
    return { to: router, data, value, spender }
  }

  if (facts.fee === null) throw new RouteRefused('wrong_fee', 'A V3 pool needs its fee tier')
  const { fee } = facts

  if (dex.style === 'router02-v3') {
    const swap = encodeFunctionData({
      abi: swapRouter02Abi,
      functionName: 'exactInputSingle',
      args: [{ tokenIn, tokenOut, fee, recipient: nativeOut ? ADDRESS_THIS : recipient, amountIn, amountOutMinimum: minOut, sqrtPriceLimitX96: 0n }],
    })
    const calls: Hex[] = [swap]
    if (nativeOut) calls.push(encodeFunctionData({ abi: swapRouter02Abi, functionName: 'unwrapWETH9', args: [minOut, recipient] }))
    // no refundETH for BNB in: an exact-input swap spends exactly msg.value, and the standard Swap page's encoder (route.sdk.test.ts) sends none either
    return { to: router, data: encodeFunctionData({ abi: swapRouter02Abi, functionName: 'multicall', args: [deadline, calls] }), value, spender }
  }

  // pcs-v3
  const swap = encodeFunctionData({
    abi: pcsV3RouterAbi,
    functionName: 'exactInputSingle',
    args: [{ tokenIn, tokenOut, fee, recipient: nativeOut ? router : recipient, deadline, amountIn, amountOutMinimum: minOut, sqrtPriceLimitX96: 0n }],
  })
  const calls: Hex[] = [swap]
  if (nativeOut) calls.push(encodeFunctionData({ abi: pcsV3RouterAbi, functionName: 'unwrapWETH9', args: [minOut, recipient] }))
  if (nativeIn) calls.push(encodeFunctionData({ abi: pcsV3RouterAbi, functionName: 'refundETH' }))
  return { to: router, data: calls.length === 1 ? swap : encodeFunctionData({ abi: pcsV3RouterAbi, functionName: 'multicall', args: [calls] }), value, spender }
}

export interface Hop {
  kind: 'v2' | 'v3'
  tokenIn: Address
  tokenOut: Address
  /** V3 only. */
  fee?: number
}

export interface DecodedTrade {
  hops: Hop[]
  /** Where the tokens end up: the swap's recipient, or the unwrap recipient when the router custodies the output. */
  payout: Address
  nativeIn: boolean
  nativeOut: boolean
  amountIn: bigint | null
  minOut: bigint | null
  deadline: bigint | null
}

const refuse = (message: string): never => {
  throw new RouteRefused('unsupported_call', message)
}

/** decodeFunctionData that answers "not a call this page builds" instead of a library error for an unknown selector. */
function safeDecode(abi: unknown, data: Hex): { functionName: string; args: readonly unknown[] } {
  try {
    return decodeFunctionData({ abi: abi as typeof swapRouter02Abi, data }) as { functionName: string; args: readonly unknown[] }
  } catch {
    return refuse('The transaction calls a function this page does not build')
  }
}

/**
 * Reads the calldata back into hops. Only the calls this module builds are understood: one swap call, optionally wrapped in the router's
 * multicall with refundETH / unwrapWETH9. Anything else (a path with several hops, an unknown function, two swaps) is refused, not guessed at.
 */
export function decodeTrade(dex: PoolDex, router: Address, data: Hex, value: bigint): DecodedTrade {
  if (lc(router) !== lc(dex.router)) throw new RouteRefused('wrong_router', 'The transaction is addressed to a contract that is not this exchange\'s router')

  if (dex.style === 'v2' || dex.style === 'v2-bakery') {
    const abi = dex.style === 'v2' ? v2RouterAbi : bakeryRouterAbi
    // the two ABIs differ only in the native function names, so decode against both shapes by selector
    const d = safeDecode(abi, data)
    const native = dex.style === 'v2' ? { inName: 'swapExactETHForTokens', outName: 'swapExactTokensForETH' } : { inName: 'swapExactBNBForTokens', outName: 'swapExactTokensForBNB' }
    let path: readonly Address[]
    let to: Address
    let amountIn: bigint | null
    let minOut: bigint
    let deadline: bigint
    if (d.functionName === 'swapExactTokensForTokens' || d.functionName === native.outName) {
      ;[amountIn, minOut, path, to, deadline] = d.args as [bigint, bigint, readonly Address[], Address, bigint]
    } else if (d.functionName === native.inName) {
      ;[minOut, path, to, deadline] = d.args as [bigint, readonly Address[], Address, bigint]
      amountIn = value
    } else {
      return refuse(`Unexpected router function ${d.functionName}`)
    }
    if (path.length !== 2) throw new RouteRefused('multi_hop', `The path has ${path.length} tokens; only a direct two-token path through the pool is allowed`)
    return {
      hops: [{ kind: 'v2', tokenIn: path[0], tokenOut: path[1] }],
      payout: to,
      nativeIn: d.functionName === native.inName,
      nativeOut: d.functionName === native.outName,
      amountIn,
      minOut,
      deadline,
    }
  }

  const abi = dex.style === 'router02-v3' ? swapRouter02Abi : pcsV3RouterAbi
  const top = safeDecode(abi, data)
  let inner: Hex[]
  let outerDeadline: bigint | null = null
  if (top.functionName === 'multicall') {
    if (dex.style === 'router02-v3') {
      outerDeadline = top.args[0] as bigint
      inner = [...(top.args[1] as readonly Hex[])]
    } else {
      inner = [...(top.args[0] as readonly Hex[])]
    }
  } else {
    inner = [data]
  }
  type SwapArgs = { tokenIn: Address; tokenOut: Address; fee: number; recipient: Address; amountIn: bigint; amountOutMinimum: bigint; deadline?: bigint }
  let swap: SwapArgs | null = null
  let unwrapTo: Address | null = null
  let refund = false
  for (const call of inner) {
    const c = safeDecode(abi, call)
    if (c.functionName === 'exactInputSingle') {
      if (swap) throw new RouteRefused('multi_hop', 'More than one swap in the transaction')
      swap = c.args[0] as SwapArgs
    } else if (c.functionName === 'unwrapWETH9') {
      unwrapTo = c.args[1] as Address
    } else if (c.functionName === 'refundETH') {
      refund = true
    } else {
      refuse(`Unexpected router call ${c.functionName}`)
    }
  }
  if (!swap) return refuse('The transaction contains no swap')
  const s: SwapArgs = swap
  const custody = lc(s.recipient) === lc(ADDRESS_THIS) || lc(s.recipient) === lc(dex.router)
  return {
    hops: [{ kind: 'v3', tokenIn: s.tokenIn, tokenOut: s.tokenOut, fee: Number(s.fee) }],
    payout: custody ? unwrapTo ?? (ZERO as Address) : s.recipient,
    nativeIn: value > 0n && lc(s.tokenIn) === lc(WBNB) && (refund || value === s.amountIn),
    nativeOut: custody && unwrapTo !== null,
    amountIn: s.amountIn,
    minOut: s.amountOutMinimum,
    deadline: s.deadline ?? outerDeadline,
  }
}

export interface ResolvePool {
  (kind: 'v2' | 'v3', factory: Address, a: Address, b: Address, fee?: number): Promise<Address>
}

export interface CheckArgs {
  dex: PoolDex
  facts: PoolFacts
  /** The pool the page promised (the competition's pool, or the pool picked on the token page). */
  expectedPool: Address
  user: Address
  trade: BuiltTrade
  tokenIn: Address
  tokenOut: Address
  amountIn: bigint
  resolvePool: ResolvePool
}

/**
 * The route check. Runs on the bytes that will be signed. Returns the pool the router will trade through; throws RouteRefused otherwise.
 * Order: who receives the call, which exchange the pool belongs to, what the calldata says, who is paid, and finally which pool the exchange's own
 * factory returns for those tokens (and fee): that one must be the expected pool.
 */
export async function checkRoute(a: CheckArgs): Promise<Address> {
  const { dex, facts, expectedPool, user, trade, tokenIn, tokenOut, resolvePool } = a
  if (lc(facts.factory) !== lc(dex.factory)) throw new RouteRefused('wrong_factory', 'The pool does not belong to the exchange whose router is being used')
  if (lc(facts.pool) !== lc(expectedPool)) throw new RouteRefused('pool_mismatch', 'The pool that was read is not the pool this page promised')
  const d = decodeTrade(dex, trade.to, trade.data, trade.value)
  if (d.hops.length !== 1) throw new RouteRefused('multi_hop', 'The route must be exactly one hop')
  const hop = d.hops[0]
  if (hop.kind !== dex.kind) throw new RouteRefused('unsupported_call', 'The route kind does not match the pool kind')
  const sides = new Set([lc(facts.token0), lc(facts.token1)])
  if (lc(hop.tokenIn) !== lc(tokenIn) || lc(hop.tokenOut) !== lc(tokenOut) || !sides.has(lc(hop.tokenIn)) || !sides.has(lc(hop.tokenOut)) || lc(hop.tokenIn) === lc(hop.tokenOut)) {
    throw new RouteRefused('wrong_tokens', 'The route does not trade exactly the two tokens of the pool')
  }
  if (dex.kind === 'v3' && (facts.fee === null || hop.fee !== facts.fee)) throw new RouteRefused('wrong_fee', 'The route uses a different fee tier than the pool')
  if (lc(d.payout) !== lc(user)) throw new RouteRefused('wrong_payout', 'The route does not pay the output to the connected wallet')
  if (d.amountIn !== null && d.amountIn !== a.amountIn) throw new RouteRefused('wrong_value', 'The amount in the transaction differs from the amount on screen')
  if (d.nativeIn && trade.value !== a.amountIn) throw new RouteRefused('wrong_value', 'The BNB sent differs from the amount on screen')
  if (!d.nativeIn && trade.value !== 0n) throw new RouteRefused('wrong_value', 'BNB is attached to a trade that should not send any')
  const resolved = await resolvePool(dex.kind, dex.factory, hop.tokenIn, hop.tokenOut, hop.fee)
  if (!resolved || lc(resolved) === lc(ZERO)) throw new RouteRefused('pool_not_found', 'The exchange has no pool for these tokens and fee')
  if (lc(resolved) !== lc(expectedPool)) {
    throw new RouteRefused('pool_mismatch', `The router would trade through ${resolved} instead of ${expectedPool}`)
  }
  return resolved
}

/** Human text for a refusal (shown in the panel). */
export function refusalText(e: unknown): string {
  if (e instanceof RouteRefused) return `Route check failed: ${e.message}. Nothing was sent.`
  return 'Route check failed. Nothing was sent.'
}

